import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { ApiError } from '@akillidernek/api-client'
import { ToastProvider } from '@akillidernek/ui'
import { bootstrapSession } from './api'
import { markBootstrapped } from './auth-store'
import { configureSentry } from './sentry'
import { router } from './router'
import { DernekSec } from './routes/dernek-sec'
import { Giris } from './routes/giris'
import { JetonEkrani, adresiTemizle, jetonYolunuCoz } from './routes/jeton-yollari'
import { useAccessToken, useBootstrapped } from './use-auth'
import { useTenantSlug } from './use-tenant'
import '@akillidernek/ui/tokens.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // İstemci hatalarını yeniden denemek anlamsız: 404 tekrar 404 döner,
        // 403 kullanıcının planı değişmeden değişmez.
        if (error instanceof ApiError && error.status < 500) return false
        return failureCount < 2
      },
    },
  },
})

// E-postadaki bağlantı (magic link, şifre sıfırlama, davet) ilk çizimde
// çözülür ve bir daha bakılmaz: jeton tüketilince adres çubuğu temizleniyor,
// yeniden okumak boş dönerdi (YES-157).
const jetonYolu = jetonYolunuCoz()

function App() {
  const token = useAccessToken()
  const hazir = useBootstrapped()
  const slug = useTenantSlug()

  // Sayfa yenilendiğinde oturum httpOnly çerezden geri kuruluyor; o bitmeden
  // giriş ekranını göstermek, açık oturumu olan kullanıcıyı boşuna korkutur.
  if (!hazir) return null
  // Şifre sıfırlama ve davet, açık oturum olsa da gösterilir: başka bir
  // hesapla giriş yapmış olabilir. Magic link ise zaten girişse anlamsız.
  if (jetonYolu && jetonYolu.yol !== 'magic') {
    return <JetonEkrani yol={jetonYolu.yol} token={jetonYolu.token} />
  }
  if (!token) {
    return jetonYolu ? (
      <Giris magicToken={jetonYolu.token} onJetonTuketildi={adresiTemizle} />
    ) : (
      <Giris />
    )
  }
  // Dernek seçilmeden panel açılmaz: her istek bir dernek bağlamı ister (YES-71).
  return slug ? <RouterProvider router={router} /> : <DernekSec />
}

configureSentry()

// Deploy sonrası eski bir chunk istenirse 404 gelir ve ekran boş kalır.
// Vite bu durumu `vite:preloadError` ile bildiriyor; tek doğru tepki, yeni
// sürümü yüklemek için sayfayı yenilemek (YES-90).
window.addEventListener('vite:preloadError', () => {
  window.location.reload()
})

// Oturumu geri kurmayı dene, sonra uygulamayı çiz.
void bootstrapSession().finally(markBootstrapped)

const container = document.getElementById('root')
if (!container) throw new Error('#root bulunamadı')

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <App />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
)
